// Checks on a navigation request against what the visitor actually said.
// The model picks a room_id itself and was ignoring the "ask when several rooms
// match" rule ("take me to the bedroom" went straight to one of three), and it
// dropped questions asked together with the move.

const FILLER = new Set([
  'the', 'a', 'an', 'to', 'me', 'us', 'take', 'go', 'show', 'let', 'lets', 's', 'please',
  'room', 'can', 'could', 'we', 'i', 'want', 'see', 'into', 'in', 'of', 'and', 'there',
  'my', 'our', 'you', 'on', 'at', 'for', 'is', 'that', 'this', 'it', 'now',
]);
const NUMBER_WORDS = {
  one: '1', first: '1', two: '2', second: '2', three: '3', third: '3',
  four: '4', fourth: '4', five: '5', fifth: '5', six: '6', sixth: '6',
};

function words(text) {
  return (String(text || '').toLowerCase().match(/[\p{L}\p{N}]+/gu) || [])
    .map(w => NUMBER_WORDS[w] || w)
    .filter(w => !FILLER.has(w));
}

const isNumber = w => /^\d+$/.test(w);

/**
 * Several rooms fit what the visitor said and nothing they said picks out the
 * room the model chose → returns the candidates ([{room_id, room_name}]) so
 * the guide asks which one. Otherwise null (go ahead).
 *
 *   "take me to the bedroom"   + Bedroom 1 / Bedroom 2      → both
 *   "take me to bedroom two"   + Bedroom 1 / Bedroom 2      → null
 *   "the principal bedroom"    + Principal Bedroom / Bed 2  → null
 *   "the kitchen"              + Kitchen / Kitchen Diner    → null (exact name)
 *   no transcript / no room word in common (other language, synonym) → null
 */
export function ambiguousRoomChoice(transcript, scenes, chosenId) {
  const said = new Set(words(transcript));
  if (!said.size) return null;
  const rooms = (scenes || []).map(s => {
    const name = s.title || s.name || String(s.id);
    return { id: String(s.id), name, words: words(name) };
  });
  const chosen = rooms.find(r => r.id === String(chosenId));
  if (!chosen || !chosen.words.length) return null;

  // Room words of the chosen room the visitor actually said. None → the model
  // matched by meaning ("master" → "Principal Bedroom"); trust it.
  const named = chosen.words.filter(w => !isNumber(w) && said.has(w));
  if (!named.length) return null;

  const candidates = rooms.filter(r => named.some(w => r.words.includes(w)));
  if (candidates.length < 2) return null;
  const others = candidates.filter(r => r !== chosen);

  // Something said that only the chosen room has ("2", "principal")
  if (chosen.words.some(w => said.has(w) && !others.some(o => o.words.includes(w)))) return null;
  // Its whole name was said and no other candidate's was ("Bedroom" vs "Bedroom 2")
  const fullySaid = r => r.words.every(w => said.has(w));
  if (fullySaid(chosen) && !others.some(fullySaid)) return null;

  return candidates.map(r => ({ room_id: r.id, room_name: r.name }));
}

const NAVIGATE = /\b(take|bring|show|go|move|head|walk|navigate)\b[^.?!]*\b(me|us|there|to)\b|\blet'?s (go|see|visit)\b/i;
const ASKS = /\b(what|what's|whats|how|which|who|when|why|is there|are there|tell me about|describe)\b/i;

/**
 * The visitor asked a question AND to be taken somewhere in one sentence
 * ("what's the flooring like in the bedroom, and take me there") → that
 * sentence, to be answered once the room has loaded. Otherwise null.
 * "Can you take me to the kitchen?" is a plain move, not a question.
 */
export function navigationQuestion(transcript) {
  const text = String(transcript || '').trim();
  if (!text || !NAVIGATE.test(text) || !ASKS.test(text)) return null;
  return text;
}
